import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, Loader2, RotateCcw, Sparkles, FileCheck2 } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { uploadChunks } from "./PdfImport";
import { FillReview } from "./FillReview";
import { WebCheck } from "./WebCheck";

const Status = ({ st }) => {
  if (!st) return null;
  const running = ["queued", "running"].includes(st.status);
  return (
    <p data-testid="original-status" className={`mt-2 text-sm ${st.status === "failed" ? "text-red-700" : "text-stone-600"}`}>
      {running && <Loader2 size={13} className="inline mr-1.5 animate-spin text-[#9A7B2C]" />}
      {running ? (st.step || "Waiting to start") : st.status === "failed" ? `Couldn't finish: ${st.note}` :
        `${st.pages ? `${st.pages} pages` : "Ready"}${st.filled !== undefined ? ` · ${st.filled} light page(s) filled` : ""}${st.verified ? ` · ${st.verified} Founder Verified place(s)` : ""}${st.note ? ` · ${st.note}` : ""}`}
    </p>
  );
};

export const OriginalPanel = ({ kit, reload }) => {
  const [pct, setPct] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const o = kit.original;
  const st = kit.original_check;
  const running = ["queued", "running"].includes(st?.status);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(reload, 8000);
    return () => clearInterval(t);
  }, [running, reload]);
  const upload = async (file) => {
    if (!file) return;
    try {
      setPct(0);
      const id = await uploadChunks(file, { bulk: true }, setPct);
      await http.post(`/kits/${kit.id}/original/${id}`);
      toast.success("Original file saved — adding verification and filling light pages now");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
    setPct(null);
  };
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.message(msg); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const btn = "inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50";
  return (
    <section data-testid="original-panel" className="mb-6 rounded-lg border border-[#C9A847]/40 bg-[#FAF7F0] p-5">
      <div className="flex items-center gap-2"><FileCheck2 size={17} className="text-[#9A7B2C]" /><b className="text-[#2B2A26]">Your original file</b></div>
      <p className="mt-1 text-sm text-stone-600">
        {o ? <>The PDF is built from <b data-testid="original-filename">{o.filename}</b> exactly as you uploaded it, with ✦ Founder Verified strips for the places ticked below{o.kind === "html" ? " and extra detail added to light pages" : ""}.</>
          : "Upload the original HTML (or PDF) of this guide and the kit will use your own pages exactly, adding only the verification strips and filling light pages."}
      </p>
      {o && <Status st={st} />}
      <div className="mt-3 flex flex-wrap gap-2">
        <input ref={input} type="file" accept=".html,.htm,.pdf" className="hidden" data-testid="original-file-input" onChange={(e) => upload(e.target.files[0])} />
        <button data-testid="original-upload-btn" disabled={pct !== null || running} onClick={() => input.current?.click()} className={`${btn} bg-[#2B2A26] text-white hover:bg-black`}>
          {pct !== null ? <><Loader2 size={12} className="animate-spin" /> Uploading {pct}%</> : <><FileUp size={12} /> {o ? "Replace original file" : "Upload original file"}</>}
        </button>
        {o?.kind === "html" && <button data-testid="original-refill-btn" disabled={busy || running} onClick={() => act(() => http.post(`/kits/${kit.id}/original-fill`), "Re-checking and filling light pages")} className={`${btn} border border-stone-300 bg-white hover:border-[#9A7B2C]`}><Sparkles size={12} /> Fill light pages again</button>}
        {o && <button data-testid="original-remove-btn" disabled={busy || running} onClick={() => act(() => http.delete(`/kits/${kit.id}/original`), "Back to the app's own layout")} className={`${btn} text-stone-500 hover:text-black`}><RotateCcw size={12} /> Stop using it</button>}
      </div>
      {o?.kind === "html" && <FillReview kit={kit} reload={reload} />}
      {o?.kind === "html" && <WebCheck kit={kit} reload={reload} />}
    </section>
  );
};
